// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

// ** API Import
import useJwt from '@src/auth/jwt/useJwt'

// ** Async Thunks
export const fetchSubscriptionStatus = createAsyncThunk(
  'pricing/fetchSubscriptionStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await useJwt.getSubscriptionStatus()
      return response.data.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch subscription status')
    }
  }
)

export const fetchPricingInfo = createAsyncThunk(
  'pricing/fetchPricingInfo',
  async (_, { rejectWithValue }) => {
    try {
      const response = await useJwt.getPricingInfo()
      return response.data.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch pricing info')
    }
  }
)

export const calculatePrice = createAsyncThunk(
  'pricing/calculatePrice',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await useJwt.calculatePrice(payload)
      return response.data.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to calculate price')
    }
  }
)

export const checkApplicationLimit = createAsyncThunk(
  'pricing/checkApplicationLimit',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await useJwt.checkApplicationLimit(payload)
      return response.data.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to check application limit')
    }
  }
)

const initialState = {
  subscriptionStatus: null,
  pricingInfo: null,
  calculatedPrice: null,
  limitCheck: null,
  selectedPlan: 'pay_per_app',
  selectedLobs: [],
  loading: false,
  error: null,
  success: false
}

export const pricingSlice = createSlice({
  name: 'pricing',
  initialState,
  reducers: {
    setSelectedPlan: (state, action) => {
      state.selectedPlan = action.payload
      state.selectedLobs = []
      state.calculatedPrice = null
    },
    setSelectedLobs: (state, action) => {
      state.selectedLobs = action.payload
      state.calculatedPrice = null
    },
    clearPricingError: (state) => {
      state.error = null
    },
    clearPricingSuccess: (state) => {
      state.success = false
    },
    resetPricingState: (state) => {
      return initialState
    }
  },
  extraReducers: (builder) => {
    // Fetch Subscription Status
    builder
      .addCase(fetchSubscriptionStatus.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchSubscriptionStatus.fulfilled, (state, action) => {
        state.loading = false
        state.subscriptionStatus = action.payload
        state.success = true
      })
      .addCase(fetchSubscriptionStatus.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    // Fetch Pricing Info
    builder
      .addCase(fetchPricingInfo.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPricingInfo.fulfilled, (state, action) => {
        state.loading = false
        state.pricingInfo = action.payload
        state.success = true
      })
      .addCase(fetchPricingInfo.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    // Calculate Price
    builder
      .addCase(calculatePrice.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(calculatePrice.fulfilled, (state, action) => {
        state.loading = false
        state.calculatedPrice = action.payload
        state.success = true
      })
      .addCase(calculatePrice.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    // Check Application Limit
    builder
      .addCase(checkApplicationLimit.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(checkApplicationLimit.fulfilled, (state, action) => {
        state.loading = false
        state.limitCheck = action.payload
        state.success = true
      })
      .addCase(checkApplicationLimit.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  }
})

export const {
  setSelectedPlan,
  setSelectedLobs,
  clearPricingError,
  clearPricingSuccess,
  resetPricingState
} = pricingSlice.actions

export default pricingSlice.reducer

